import fs from 'fs';
import path from 'path';

/**
 * LLM Usage Logger - Tracks token usage, costs, and timing for all LLM API calls
 */

export interface LLMUsageEntry {
    timestamp: string;
    requestId: string;
    provider: string;
    model: string;
    method: string;  // 'text' | 'stream' | 'streamWithTools'
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens?: number;
    cacheWriteTokens?: number;
    totalTokens: number;
    costUSD: number;
    durationMs: number;
    toolCalls?: number;
    success: boolean;
    error?: string;
}

// Pricing per 1M tokens
const PRICING: Record<string, { input: number; output: number; cacheRead?: number; cacheWrite?: number }> = {
    // Anthropic (December 2025)
    'claude-opus-4-5': { input: 5.00, output: 25.00, cacheRead: 0.50, cacheWrite: 6.25 },
    'claude-opus-4-5-20251101': { input: 5.00, output: 25.00, cacheRead: 0.50, cacheWrite: 6.25 },
    'claude-sonnet-4-5': { input: 3.00, output: 15.00, cacheRead: 0.30, cacheWrite: 3.75 },
    'claude-sonnet-4-5-20250929': { input: 3.00, output: 15.00, cacheRead: 0.30, cacheWrite: 3.75 },
    'claude-haiku-4-5': { input: 1.00, output: 5.00, cacheRead: 0.10, cacheWrite: 1.25 },
    'claude-haiku-4-5-20251001': { input: 1.00, output: 5.00, cacheRead: 0.10, cacheWrite: 1.25 },
    'claude-3-5-sonnet-20241022': { input: 3.00, output: 15.00, cacheRead: 0.30, cacheWrite: 3.75 },
    'claude-3-5-haiku-20241022': { input: 1.00, output: 5.00, cacheRead: 0.10, cacheWrite: 1.25 },
    'claude-3-opus-20240229': { input: 15.00, output: 75.00, cacheRead: 1.50, cacheWrite: 18.75 },
    'claude-3-sonnet-20240229': { input: 3.00, output: 15.00, cacheRead: 0.30, cacheWrite: 3.75 },
    'claude-3-haiku-20240307': { input: 0.25, output: 1.25, cacheRead: 0.03, cacheWrite: 0.30 },

    // OpenAI (December 2025)
    'gpt-5': { input: 1.25, output: 10.00 },
    'gpt-5-mini': { input: 0.25, output: 2.00 },
    'gpt-4o': { input: 5.00, output: 15.00 }, // Updated pricing as of late 2025
    'gpt-4o-mini': { input: 0.15, output: 0.60 },
    'gpt-4-turbo': { input: 10.00, output: 30.00 },
    'gpt-4': { input: 30.00, output: 60.00 },
    'gpt-3.5-turbo': { input: 0.50, output: 1.50 },

    // Google Gemini (January 2026)
    'gemini-3-pro-preview': { input: 2.00, output: 12.00 }, // New Gemini 3
    'gemini-3-flash-preview': { input: 0.50, output: 3.00 }, // For prompts ≤200K tokens, 2x for >200K
    'gemini-2.5-flash': { input: 0.30, output: 2.50 }, // Paid tier: $0.30 input (text/image/video), $2.50 output (includes thinking)
    'gemini-2.5-flash-lite': { input: 0.10, output: 0.40 },
    'gemini-2.0-flash': { input: 0.10, output: 0.40 },
    'gemini-2.0-flash-lite': { input: 0.075, output: 0.30 },
    'gemini-1.5-pro': { input: 1.25, output: 5.00 },
    'gemini-1.5-flash': { input: 0.075, output: 0.30 },

    // Groq (December 2025)
    'llama-3.3-70b-versatile': { input: 0.59, output: 0.79 },
    'llama-3.1-70b-versatile': { input: 0.59, output: 0.79 },
    'llama-3.1-8b-instant': { input: 0.05, output: 0.08 },
    'llama-4-scout-17b-16e': { input: 0.11, output: 0.34 },
    'llama-4-maverick-17b-128e': { input: 0.20, output: 0.60 },
    'mixtral-8x7b-32768': { input: 0.27, output: 0.27 },
    'qwen3-32b': { input: 0.29, output: 0.59 },
};

// Default pricing for unknown models
const DEFAULT_PRICING = { input: 3.00, output: 15.00 };

class LLMUsageLogger {
    private logStream: fs.WriteStream | null = null;
    private logPath: string;
    private enabled: boolean;
    private sessionStats = {
        totalCalls: 0,
        totalInputTokens: 0,
        totalOutputTokens: 0,
        totalCacheReadTokens: 0,
        totalCacheWriteTokens: 0,
        totalCostUSD: 0,
        totalDurationMs: 0,
    };

    constructor() {
        // Get log path from environment or use default
        this.logPath = process.env.LLM_USAGE_LOG_PATH || path.join(process.cwd(), 'llm-usage-logs.log');
        this.enabled = process.env.LLM_USAGE_LOGGING !== 'false';

        if (this.enabled) {
            this.initLogStream();
        }
    }

    private initLogStream(): void {
        try {
            // Ensure directory exists
            const dir = path.dirname(this.logPath);
            if (!fs.existsSync(dir)) {
                fs.mkdirSync(dir, { recursive: true });
            }

            this.logStream = fs.createWriteStream(this.logPath, { flags: 'a' });

            // Write header if file is new/empty
            if (!fs.existsSync(this.logPath) || fs.statSync(this.logPath).size === 0) {
                this.writeHeader();
            }
        } catch (error) {
            console.error('[LLM-Usage-Logger] Failed to initialize log stream:', error);
            this.enabled = false;
        }
    }

    private writeHeader(): void {
        const header = `
================================================================================
LLM USAGE LOG - Session Started: ${new Date().toISOString()}
================================================================================
Format: [TIMESTAMP] [REQUEST_ID] [PROVIDER/MODEL] [METHOD]
        Tokens: IN=input OUT=output CACHE_R=cache_read CACHE_W=cache_write TOTAL=total
        Cost: $X.XXXXXX | Time: Xms
================================================================================

`;
        this.logStream?.write(header);
    }

    /**
     * Calculate cost based on token usage and model
     */
    calculateCost(
        model: string,
        inputTokens: number,
        outputTokens: number,
        cacheReadTokens = 0,
        cacheWriteTokens = 0
    ): number {
        // Find pricing for model (exact, then normalized partial match).
        // Normalize so OpenRouter slugs like "anthropic/claude-haiku-4.5" match
        // table keys like "claude-haiku-4-5": drop any "provider/" prefix and
        // treat "." and "-" as equivalent version separators.
        let pricing = PRICING[model];
        if (!pricing) {
            const norm = (s: string) => s.toLowerCase().replace(/^[^/]+\//, '').replace(/\./g, '-');
            const m = norm(model);
            for (const [key, value] of Object.entries(PRICING)) {
                const k = norm(key);
                if (m.includes(k) || k.includes(m)) {
                    pricing = value;
                    break;
                }
            }
        }
        pricing = pricing || DEFAULT_PRICING;

        const inputCost = (inputTokens / 1_000_000) * pricing.input;
        const outputCost = (outputTokens / 1_000_000) * pricing.output;
        const cacheReadCost = (cacheReadTokens / 1_000_000) * (pricing.cacheRead || pricing.input * 0.1);
        const cacheWriteCost = (cacheWriteTokens / 1_000_000) * (pricing.cacheWrite || pricing.input * 1.25);

        return inputCost + outputCost + cacheReadCost + cacheWriteCost;
    }

    /**
     * Log an LLM API call
     */
    log(entry: LLMUsageEntry): void {
        if (!this.enabled) return;

        // Update session stats
        this.sessionStats.totalCalls++;
        this.sessionStats.totalInputTokens += entry.inputTokens;
        this.sessionStats.totalOutputTokens += entry.outputTokens;
        this.sessionStats.totalCacheReadTokens += entry.cacheReadTokens || 0;
        this.sessionStats.totalCacheWriteTokens += entry.cacheWriteTokens || 0;
        this.sessionStats.totalCostUSD += entry.costUSD;
        this.sessionStats.totalDurationMs += entry.durationMs;

        // Format log entry
        const cacheInfo = entry.cacheReadTokens || entry.cacheWriteTokens
            ? ` CACHE_R=${entry.cacheReadTokens || 0} CACHE_W=${entry.cacheWriteTokens || 0}`
            : '';

        const toolInfo = entry.toolCalls ? ` | Tools: ${entry.toolCalls}` : '';
        const errorInfo = entry.error ? ` | ERROR: ${entry.error}` : '';
        const status = entry.success ? '✓' : '✗';

        // Calculate cache savings
        let cacheStatus = '';
        if (entry.cacheReadTokens && entry.cacheReadTokens > 0) {
            const savedCost = (entry.cacheReadTokens / 1_000_000) * 2.70; // Difference between regular and cache price
            cacheStatus = ` ⚡ CACHE HIT! Saved ~$${savedCost.toFixed(4)}`;
        } else if (entry.cacheWriteTokens && entry.cacheWriteTokens > 0) {
            cacheStatus = ' 📝 Cache created (next request will be cheaper)';
        }

        const logLine = `[${entry.timestamp}] [${entry.requestId}] ${status} ${entry.provider}/${entry.model} [${entry.method}]
    Tokens: IN=${entry.inputTokens} OUT=${entry.outputTokens}${cacheInfo} TOTAL=${entry.totalTokens}
    Cost: $${entry.costUSD.toFixed(6)} | Time: ${entry.durationMs}ms${toolInfo}${errorInfo}${cacheStatus}
`;

        this.logStream?.write(logLine);
    }

    /**
     * Log session summary (call at end of request)
     */
    logSessionSummary(requestContext?: string): void {
        if (!this.enabled || this.sessionStats.totalCalls === 0) return;

        // Calculate cache savings
        const cacheReadSavings = (this.sessionStats.totalCacheReadTokens / 1_000_000) * 2.70; // Saved per 1M tokens
        const hasCaching = this.sessionStats.totalCacheReadTokens > 0 || this.sessionStats.totalCacheWriteTokens > 0;

        let cacheSection = '';
        if (hasCaching) {
            cacheSection = `
Cache Statistics:
  Cache Read Tokens: ${this.sessionStats.totalCacheReadTokens.toLocaleString()}${this.sessionStats.totalCacheReadTokens > 0 ? ' ⚡' : ''}
  Cache Write Tokens: ${this.sessionStats.totalCacheWriteTokens.toLocaleString()}${this.sessionStats.totalCacheWriteTokens > 0 ? ' 📝' : ''}
  Estimated Savings: $${cacheReadSavings.toFixed(4)}`;
        }

        const summary = `
--------------------------------------------------------------------------------
SESSION SUMMARY${requestContext ? ` (${requestContext})` : ''}
--------------------------------------------------------------------------------
Total LLM Calls: ${this.sessionStats.totalCalls}
Total Input Tokens: ${this.sessionStats.totalInputTokens.toLocaleString()}
Total Output Tokens: ${this.sessionStats.totalOutputTokens.toLocaleString()}
Total Tokens: ${(this.sessionStats.totalInputTokens + this.sessionStats.totalOutputTokens).toLocaleString()}
Total Cost: $${this.sessionStats.totalCostUSD.toFixed(6)}
Total Time: ${this.sessionStats.totalDurationMs}ms (${(this.sessionStats.totalDurationMs / 1000).toFixed(2)}s)
Avg Cost/Call: $${(this.sessionStats.totalCostUSD / this.sessionStats.totalCalls).toFixed(6)}
Avg Time/Call: ${Math.round(this.sessionStats.totalDurationMs / this.sessionStats.totalCalls)}ms${cacheSection}
--------------------------------------------------------------------------------

`;
        this.logStream?.write(summary);
    }

    /**
     * Reset session stats (call at start of new user request)
     */
    resetSession(): void {
        this.sessionStats = {
            totalCalls: 0,
            totalInputTokens: 0,
            totalOutputTokens: 0,
            totalCacheReadTokens: 0,
            totalCacheWriteTokens: 0,
            totalCostUSD: 0,
            totalDurationMs: 0,
        };
    }

    /**
     * Reset the log file for a new request (clears previous logs)
     * Call this at the start of each USER_PROMPT_REQ
     */
    resetLogFile(requestContext?: string): void {
        if (!this.enabled) return;

        try {
            // Close existing stream
            if (this.logStream) {
                this.logStream.end();
                this.logStream = null;
            }

            // Truncate the file by opening in write mode (not append)
            this.logStream = fs.createWriteStream(this.logPath, { flags: 'w' });

            // Write new header
            const header = `
================================================================================
LLM USAGE LOG - Request Started: ${new Date().toISOString()}
${requestContext ? `Context: ${requestContext}` : ''}
================================================================================
Format: [TIMESTAMP] [REQUEST_ID] [PROVIDER/MODEL] [METHOD]
        Tokens: IN=input OUT=output CACHE_R=cache_read CACHE_W=cache_write TOTAL=total
        Cost: $X.XXXXXX | Time: Xms
================================================================================

`;
            this.logStream.write(header);

            // Reset session stats
            this.resetSession();
        } catch (error) {
            console.error('[LLM-Usage-Logger] Failed to reset log file:', error);
        }
    }

    /**
     * Get current session stats
     */
    getSessionStats() {
        return { ...this.sessionStats };
    }

    /**
     * Generate a unique request ID
     */
    generateRequestId(): string {
        return `req-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
    }
}

// Export singleton instance
export const llmUsageLogger = new LLMUsageLogger();
